/* ============================================================
   EXON DROPS — GIFT COLLECTION MEMBERSHIP (Stage 4).

   The Gift goal is measured over the selected option's COLLECTION, and the
   browser cannot know which cart lines belong to it: /cart.js carries no
   collections, and `product.collections` omits hidden Online Store
   collections. So membership is read from the one place that knows it — a
   Section Rendering fragment of sections/drops-membership.liquid rendered on
   the scope's own collection route — and it is only ever accepted when it
   describes EXACTLY the cart the store currently holds.

   Ownership stays where it was:
   · exon-store.js owns the cart (window.EXON.cart); this file never writes
     it and never fetches /cart.js.
   · drops.js owns the Drop state; this file only reads its published,
     already-normalised selection (gift.selected_option / option.goal.scope).
   · This file fetches ONE thing: /collections/<scope.handle>?sections=
     drops-membership, same-origin. No drops API, no session ref, no code.

   Output: window.EXON.drops.getGiftMembership() →
     { signature, collection_gid, members: { <line key>: boolean } } | null
   and an `exon:drops:membership` event whenever a fetch settles, so the
   goal rail can repaint. NULL IS UNKNOWN: an in-flight read, a hidden
   collection (404), any non-OK answer, a network or parse failure, a stale
   scope, or a fragment that disagrees with the cart in any line.
   ============================================================ */
(function () {
  'use strict';

  if (window.__exonDropsMembershipInit) return;
  window.__exonDropsMembershipInit = true;
  window.EXON = window.EXON || {};

  var SECTION_ID = 'drops-membership';
  var COLLECTION_GID_RE = /^gid:\/\/shopify\/Collection\/(\d+)$/;
  var SCRIPT_RE = /<script\b[^>]*\bdata-drops-membership\b[^>]*>([\s\S]*?)<\/script\s*>/i;
  var MAX_CACHE = 16;

  /* key -> parsed fragment object, or null for a settled failure. A failure
     is cached for the SAME key on purpose: every exon:cart would otherwise
     re-ask a route that just answered 404. A changed cart or scope is a new
     key and is asked again. */
  var cache = Object.create(null);
  var cacheOrder = [];
  var inflight = Object.create(null);

  function whole(v) {
    return (typeof v === 'number' && isFinite(v) && v >= 0 && Math.floor(v) === v) ? v : null;
  }

  /* The current identity: entitled state, a selected option whose Stage 3
     goal carries a scope, and a cart snapshot with a token. Everything the
     cache key and the join depend on, read fresh every time. */
  function current() {
    var d = window.EXON.drops;
    var s = null;
    try { s = d && typeof d.get === 'function' ? d.get() : (d && d.state) || null; } catch (e) { s = null; }
    if (!s || s.entitled !== true) return null;
    var g = s.gift;
    if (!g || !Array.isArray(g.options)) return null;
    var sel = whole(g.selected_option);
    var option = sel === null ? null : g.options[sel];
    var goal = option && option.goal;
    var scope = goal && goal.scope;
    var m = scope && COLLECTION_GID_RE.exec(scope.collection_gid || '');
    if (!m || typeof scope.handle !== 'string' || !scope.handle) return null;
    var collectionId = Number(m[1]);
    if (!Number.isSafeInteger(collectionId)) return null;
    var cart = window.EXON.cart && window.EXON.cart.state;
    if (!cart || !Array.isArray(cart.items) || typeof cart.token !== 'string' || !cart.token) return null;
    var parts = [];
    for (var i = 0; i < cart.items.length; i++) {
      var line = cart.items[i];
      if (!line || typeof line.key !== 'string' || !line.key) return null;
      parts.push(line.key + ':' + line.quantity);
    }
    parts.sort();
    return {
      key: scope.collection_gid + '|' + cart.token + '|' + parts.join(','),
      collection_gid: scope.collection_gid,
      collection_id: collectionId,
      handle: scope.handle,
      cart: cart
    };
  }

  /* The Section Rendering answer is JSON {"drops-membership": "<div …>…
     <script type=application/json data-drops-membership>{…}</script>…</div>"}.
     The HTML string is never inserted into a document and nothing in it is
     executed: the one JSON script body is cut out and parsed as data. */
  function parseSectionResponse(body) {
    if (!body || typeof body !== 'object' || Array.isArray(body)) return null;
    var html = body[SECTION_ID];
    if (typeof html !== 'string' || !html) return null;
    var match = SCRIPT_RE.exec(html);
    if (!match) return null;
    var data = null;
    try { data = JSON.parse(match[1]); } catch (e) { return null; }
    if (!data || typeof data !== 'object' || Array.isArray(data) || !Array.isArray(data.lines)) return null;
    return data;
  }

  function sameId(a, b) {
    return typeof a === 'number' && Number.isSafeInteger(a) && Number(b) === a;
  }

  /* THE JOIN. Accepted only when the fragment is this scope's route, has the
     cart owner's whole item count and quantity sum, has the same number of
     lines, and every current line's key, variant, product and quantity match
     a fragment line. Anything else is unknown. */
  function join(fragment, cur) {
    if (!fragment || !cur) return null;
    if (fragment.route_collection_id !== cur.collection_id ||
        fragment.route_collection_handle !== cur.handle) return null;
    var lines = fragment.lines;
    var items = cur.cart.items;
    if (!Array.isArray(lines) || lines.length !== items.length) return null;
    var itemCount = whole(fragment.item_count);
    if (itemCount === null || itemCount !== cur.cart.item_count) return null;
    var byKey = Object.create(null);
    var quantitySum = 0;
    for (var i = 0; i < lines.length; i++) {
      var f = lines[i];
      if (!f || typeof f !== 'object' || typeof f.key !== 'string' || !f.key ||
          byKey[f.key] || typeof f.member !== 'boolean' || whole(f.quantity) === null) return null;
      quantitySum += f.quantity;
      byKey[f.key] = f;
    }
    if (quantitySum !== itemCount) return null;
    var members = {};
    for (var j = 0; j < items.length; j++) {
      var line = items[j];
      var hit = byKey[line.key];
      if (!hit || !sameId(hit.variant_id, line.variant_id) || !sameId(hit.product_id, line.product_id) ||
          typeof hit.quantity !== 'number' || hit.quantity !== line.quantity) return null;
      members[line.key] = hit.member;
    }
    return { signature: cur.key, collection_gid: cur.collection_gid, members: members };
  }

  function remember(key, value) {
    if (!(key in cache)) {
      cacheOrder.push(key);
      while (cacheOrder.length > MAX_CACHE) delete cache[cacheOrder.shift()];
    }
    cache[key] = value;
  }

  function announce() {
    try {
      document.dispatchEvent(new CustomEvent('exon:drops:membership', { detail: null }));
    } catch (e) { /* the rail repaints on its own events too */ }
  }

  function evaluate() {
    var cur = current();
    if (!cur) return;
    if (cur.key in cache || inflight[cur.key]) return;
    var key = cur.key;
    var url = '/collections/' + encodeURIComponent(cur.handle) + '?sections=' + SECTION_ID;
    var p;
    try {
      p = fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' } });
    } catch (e) {
      p = Promise.reject(e);
    }
    inflight[key] = Promise.resolve(p).then(function (res) {
      if (!res || !res.ok) return null;
      return res.json().then(parseSectionResponse);
    }).catch(function () {
      return null;
    }).then(function (fragment) {
      delete inflight[key];
      remember(key, fragment);
      /* RECHECK AT JOIN: the cart or selection may have moved while this was
         in flight. getGiftMembership() always joins against the CURRENT
         identity, and a moved identity is asked for here. */
      evaluate();
      announce();
    });
  }

  function getGiftMembership() {
    var cur = current();
    if (!cur || inflight[cur.key] || !(cur.key in cache)) return null;
    var joined = join(cache[cur.key], cur);
    if (!joined) return null;
    var members = {};
    for (var k in joined.members) {
      if (Object.prototype.hasOwnProperty.call(joined.members, k)) members[k] = joined.members[k];
    }
    return { signature: joined.signature, collection_gid: joined.collection_gid, members: members };
  }

  var api = window.EXON.drops = window.EXON.drops || {};
  api.getGiftMembership = getGiftMembership;

  try {
    if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
      document.addEventListener('exon:cart', evaluate);
      document.addEventListener('exon:drops:state', evaluate);
    }
    evaluate();
  } catch (e) {
    try { console.error('[exon:drops] membership_boot_threw', e); } catch (er) {}
  }

  /* exported for direct testing, the same way the other drops islands export
     their pure stages */
  window.ExonDropsMembership = { parse: parseSectionResponse, join: join };
})();
